import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Loader2, Send } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { api, formatApiError } from "@/lib/api";
import { SERVICES } from "@/data/content";

const empty = { name: "", email: "", phone: "", company: "", service: "", message: "" };
const INSTAGRAM = "https://www.instagram.com/digital.madhuraa";
const WHATSAPP = "919730527427";

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[var(--dm-alt)] scroll-mt-20" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12">
        {/* left info */}
        <div>
          <Reveal>
            <span className="overline text-[var(--dm-purple)]">Get in Touch</span>
            <h2 className="font-head font-extrabold tracking-tighter text-4xl sm:text-5xl text-slate-900 mt-4 leading-[1.05]">
              Let&apos;s build your <span className="text-gradient">growth story</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600 max-w-md">
              Tell us where you want to go. We&apos;ll show you how to get there — fast.
            </p>
          </Reveal>

          <div className="mt-9 space-y-4">
            {[
              { Icon: Mail, label: "Email", value: "madhuraadigital16@gmail.com", href: "mailto:madhuraadigital16@gmail.com" },
              { Icon: Phone, label: "Phone / WhatsApp", value: "+91 97305 27427", href: `https://wa.me/${WHATSAPP}` },
              { Icon: MapPin, label: "Office", value: "Pune, Maharashtra, India", href: "#map" },
            ].map(({ Icon, label, value, href }) => (
              <Reveal key={label} delay={0.05}>
                <a href={href} className="flex items-center gap-4 rounded-2xl bg-white border border-slate-100 p-4 hover:shadow-lg transition-shadow group">
                  <span className="w-11 h-11 rounded-xl grid place-items-center text-white group-hover:scale-110 transition-transform" style={{ background: "linear-gradient(135deg,#6C2BD9,#2563EB)" }}>
                    <Icon size={19} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
                    <div className="font-head font-bold text-slate-900">{value}</div>
                  </div>
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.05}>
              <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="inline-flex items-center gap-3 rounded-full bg-white border border-slate-100 px-5 py-3 hover:shadow-lg transition-shadow">
                <Instagram size={18} className="text-[var(--dm-purple)]" />
                <span className="font-head font-bold text-slate-900">@digital.madhuraa</span>
              </a>
            </Reveal>
          </div>

          {/* map */}
          <Reveal delay={0.1} className="mt-6">
            <div id="map" className="rounded-2xl overflow-hidden border border-slate-100 h-56 shadow-sm">
              <iframe
                title="Digital Madhuraa Office"
                src="https://www.google.com/maps?q=Pune,Maharashtra,India&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>

        {/* form */}
        <Reveal>
          <form onSubmit={submit} className="rounded-[1.75rem] bg-white border border-slate-200 p-7 sm:p-9 shadow-xl shadow-slate-200/50" data-testid="contact-form">
            <h3 className="font-head font-extrabold text-2xl text-slate-900 mb-6">Send us a message</h3>
            <div className="space-y-4">
              <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="contact-name" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
              <div className="grid sm:grid-cols-2 gap-4">
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="contact-email" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
                <input value={form.phone} onChange={set("phone")} placeholder="Phone" data-testid="contact-phone" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.company} onChange={set("company")} placeholder="Company (optional)" data-testid="contact-company" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
                <select value={form.service} onChange={set("service")} data-testid="contact-service" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600 focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100 bg-white">
                  <option value="">Service of interest</option>
                  {SERVICES.map((s) => <option key={s.key} value={s.title}>{s.title}</option>)}
                </select>
              </div>
              <textarea required value={form.message} onChange={set("message")} placeholder="How can we help?" rows={4} data-testid="contact-message" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
              <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full rounded-full py-4 font-head font-bold text-white flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-70" style={{ background: "linear-gradient(135deg,#6C2BD9,#2563EB)" }}>
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Send Message <Send size={17} /></>}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
